import { ITEMS } from '../data/presets';
import type { DieFace } from '../combat/dice';
import { grantExp } from './hero';
import type { BoardState, Hero, Monster, SaveData } from './model';
import { addMemory } from './progression';
import { towerMonster } from './tower';
import { t } from '../../i18n';

/**
 * The dice board ("notebook board game"): 20 squares round the page. A roll
 * moves the hero; the square pays coins, EXP or an item, warps, gives another
 * roll, or starts a fight. Rolls: DAILY_ROLLS free each day (topped up, not
 * stacked) plus one per battle won, at most MAX_ROLLS held.
 */

export type SquareKind = 'start' | 'coins' | 'bigcoins' | 'exp' | 'chest' | 'monster' | 'boss' | 'forward' | 'back' | 'extra' | 'mystery';

export const BOARD: readonly SquareKind[] = [
  'start', 'coins', 'monster', 'exp', 'chest', 'back', 'coins', 'mystery', 'monster', 'extra',
  'bigcoins', 'exp', 'monster', 'forward', 'chest', 'coins', 'back', 'mystery', 'boss', 'exp',
];
export const DAILY_ROLLS = 5;
export const MAX_ROLLS = 10;
export const LAP_COINS = 100;
const FORWARD = 3;
const BACK = 2;

export const newBoard = (): BoardState => ({ pos: 0, rolls: DAILY_ROLLS, day: '' });

/** Tops the free rolls up once per day. */
export function refreshBoard(save: SaveData, today: string): void {
  if (save.board.day === today) return;
  save.board.day = today;
  save.board.rolls = Math.max(save.board.rolls, DAILY_ROLLS);
}

/** A won battle earns one more roll. */
export function grantBoardRoll(save: SaveData): void {
  save.board.rolls = Math.min(MAX_ROLLS, save.board.rolls + 1);
}

export const BOARD_PREFIX = 'board:';

/** Board fights reuse the tower's monster maker at the hero's level (bosses a few levels up). */
export function boardMonster(level: number, boss: boolean): Monster {
  const floor = Math.max(1, boss ? level + 3 : level);
  return { ...towerMonster(boss ? Math.ceil(floor / 5) * 5 : floor), id: `${BOARD_PREFIX}${floor}${boss ? ':boss' : ''}`, source: 'board' };
}

/** Parses board:N or board:N:boss back into a monster. */
export function boardMonsterFromId(id: string): Monster | null {
  const m = /^board:(\d{1,4})(:boss)?$/.exec(id);
  if (!m) return null;
  const floor = Number(m[1]);
  if (floor < 1) return null;
  const boss = m[2] !== undefined;
  return boardMonster(boss ? floor - 3 : floor, boss);
}

export type BoardReward =
  | { kind: 'coins'; coins: number }
  | { kind: 'exp'; exp: number; levelsGained: number }
  | { kind: 'item'; item: string }
  | { kind: 'extra' }
  | { kind: 'fight'; monsterId: string; boss: boolean }
  | { kind: 'move'; by: number }
  | { kind: 'none' };

export interface BoardTurn {
  face: DieFace;
  /** Every square stepped on, in order (for the animation). */
  path: number[];
  landed: SquareKind;
  /** What the landing square did (a mystery square resolves to one of the others). */
  reward: BoardReward;
  /** A forward/back square moves the hero again; this is what the second square did. */
  then: BoardReward | null;
  lap: boolean;
}

/** Spends one roll and plays it. Mutates save and hero. Null when there are no rolls left. */
export function takeTurn(save: SaveData, hero: Hero, face: DieFace, random: () => number = Math.random): BoardTurn | null {
  const b = save.board;
  if (b.rolls <= 0) return null;
  b.rolls--;
  save.counters.diceRolls++;
  const path: number[] = [];
  let lap = false;
  const step = (by: number): void => {
    for (let i = 0; i < Math.abs(by); i++) {
      const next = (b.pos + Math.sign(by) + BOARD.length) % BOARD.length;
      if (by > 0 && next === 0) lap = true;
      b.pos = next;
      path.push(next);
    }
  };
  step(face);
  const landed = BOARD[b.pos]!;
  const reward = resolve(save, hero, landed, random);
  let then: BoardReward | null = null;
  if (reward.kind === 'move') {
    step(reward.by);
    // The warp's square pays too, unless it is another warp.
    const second = BOARD[b.pos]!;
    then = second === 'forward' || second === 'back' ? { kind: 'none' } : resolve(save, hero, second, random);
  }
  if (lap) {
    save.coins += LAP_COINS;
    save.counters.boardLaps++;
    addMemory(save, { kind: 'quest', title: t('Round the board!'), text: t('Back to the start: +{coins} coins.', { coins: LAP_COINS }), sticker: 'star' });
  }
  return { face, path, landed, reward, then, lap };
}

function resolve(save: SaveData, hero: Hero, kind: SquareKind, random: () => number): BoardReward {
  const L = hero.level;
  switch (kind) {
    case 'coins': {
      const coins = 20 + 2 * L;
      save.coins += coins;
      return { kind: 'coins', coins };
    }
    case 'bigcoins': {
      const coins = 60 + 5 * L;
      save.coins += coins;
      return { kind: 'coins', coins };
    }
    case 'exp': {
      const exp = 30 + 8 * L;
      return { kind: 'exp', exp, levelsGained: grantExp(hero, exp) };
    }
    case 'chest': {
      // An item the hero could already buy and does not own yet; coins when there is none.
      // (Shop prices went up about 3x in v0.7, so the price window did too.)
      const pool = ITEMS.filter((i) => !save.items.includes(i.id) && i.price <= 3 * (120 + 20 * L));
      if (pool.length === 0) {
        const coins = 50 + 4 * L;
        save.coins += coins;
        return { kind: 'coins', coins };
      }
      const item = pool[Math.floor(random() * pool.length)]!.id;
      save.items.push(item);
      return { kind: 'item', item };
    }
    case 'extra':
      save.board.rolls = Math.min(MAX_ROLLS, save.board.rolls + 1);
      return { kind: 'extra' };
    case 'monster':
      return { kind: 'fight', monsterId: boardMonster(L, false).id, boss: false };
    case 'boss':
      return { kind: 'fight', monsterId: boardMonster(L, true).id, boss: true };
    case 'forward':
      return { kind: 'move', by: FORWARD };
    case 'back':
      return { kind: 'move', by: -BACK };
    case 'mystery': {
      const options: SquareKind[] = ['coins', 'bigcoins', 'exp', 'chest', 'extra'];
      return resolve(save, hero, options[Math.floor(random() * options.length)]!, random);
    }
    default:
      return { kind: 'none' };
  }
}
